/**
 * Created by Ivan on 17.05.2026
 */

const SeasonPassLogic = function (mission) {
    cleverapps.EventEmitter.call(this);

    this.mission = mission;
    this.questPack = new SeasonPassQuestPack(this);

    this.load(mission.details || {});

    this.questPack.scheduleRefresh();

    if (this.mission.isRunning()) {
        const tier = this.getTierSuffix();
        this.logEvent(cleverapps.EVENTS.SEASON_PASS.DAU + tier, { once: true, key: "seasonpass_dau" });
        this.logEvent(cleverapps.EVENTS.SEASON_PASS.STAGE + tier, { value: this.stage, once: true, key: "seasonpass_stage" });
        this.mission.logEvent(Mission.EVENTS.MISSION_STAGE, { itemId: `${this.stage}`, once: true });
    }
};

SeasonPassLogic.prototype = Object.create(cleverapps.EventEmitter.prototype);
SeasonPassLogic.prototype.constructor = SeasonPassLogic;

SeasonPassLogic.prototype.load = function (data) {
    this.period = data.period || SeasonPassLogic.getPeriod() || SeasonPassLogic.MONTHS[0];
    this.stage = parseInt(data.stage) || 0;
    this.points = parseInt(data.points) || 0;
    this.claimed = parseInt(data.claimed) || 0;
    this.claimedVip = parseInt(data.claimedVip) || 0;
    this.vip = parseInt(data.vip) || 0;
    this.chestClaims = parseInt(data.chestClaims) || 0;
    this.nudges = parseInt(data.nudges) || 0;
    this.pending = data.pending || null;

    if (!bundles[`season_pass_${this.period}_config`]) {
        this.mission.complete();
        return;
    }

    this.questPack.load(data.pack || {});

    if (!bundles[`season_pass_${this.period}`]) {
        this.mission.complete();
    }
};

SeasonPassLogic.prototype.save = function (details) {
    details.period = this.period || undefined;
    details.stage = this.stage || undefined;
    details.points = this.points || undefined;
    details.claimed = this.claimed || undefined;
    details.claimedVip = this.claimedVip || undefined;
    details.vip = this.vip || undefined;
    details.chestClaims = this.chestClaims || undefined;
    details.nudges = this.nudges || undefined;
    details.pending = this.pending || undefined;
    details.pack = this.questPack.save();
};

SeasonPassLogic.prototype.getStagesConfig = function () {
    return bundles[`season_pass_${this.period}_config`].jsons[`season_pass_${this.period}_stages_json`].getJson();
};

SeasonPassLogic.prototype.getStages = function () {
    return this.getStagesConfig().map((stage) => ({
        ...stage,
        free: this.resolveRewards(stage.free),
        vip: this.resolveRewards(stage.vip)
    }));
};

SeasonPassLogic.prototype.resolveRewards = function (rewards) {
    if (!rewards || !rewards.units) {
        return rewards;
    }

    return {
        ...rewards,
        units: cleverapps.toArray(rewards.units).map((unit) => {
            if (!unit.type) {
                return unit;
            }

            return { code: UnitsLibraryHelper.GetInstance("main").getCurrentOfType(unit.type, "main"), stage: unit.stage, amount: unit.amount };
        })
    };
};

SeasonPassLogic.prototype.getChestReward = function () {
    return this.resolveRewards(bundles[`season_pass_${this.period}_config`].jsons[`season_pass_${this.period}_merge2chest_json`].getJson().reward);
};

SeasonPassLogic.prototype.getStageCost = function (stage) {
    // raw config: the cost needs only points, and resolving the stages' dynamic units
    // requires the map - addPoints fires off it too (miner, spendHard adapters)
    const next = this.getStagesConfig()[stage + 1];
    return next && next.points || 0;
};

SeasonPassLogic.prototype.addPoints = function (amount) {
    if (!this.canEarnPoints()) {
        return;
    }

    this.mission.logEvent(Mission.EVENTS.MISSION_POINTS, { value: amount });
    this.mission.logEvent(Mission.EVENTS.MISSION_ACTION, { once: true });
    cleverapps.playSession.logAvg(cleverapps.EVENTS.SEASON_PASS.POINTS + this.getTierSuffix(), { value: amount });

    this.points += amount;

    if (!this.isCompleted()) {
        const prevStage = this.stage;
        let cost = this.getStageCost(this.stage);
        while (this.stage < SeasonPassLogic.STAGES_AMOUNT - 1 && this.points >= cost) {
            this.points -= cost;
            this.stage += 1;
            this.trigger("stageUp", this.stage);
            cost = this.getStageCost(this.stage);
        }

        if (this.stage !== prevStage) {
            this.mission.logEvent(Mission.EVENTS.MISSION_STAGE, { itemId: `${this.stage}` });

            if (this.isCompleted()) {
                this.mission.logEvent(Mission.EVENTS.MISSION_FINISH, { once: true });
            }
        }

        if (this.isCompleted() && !this.isSuperUnlocked()) {
            this.points = 0;
        }
    }

    this.mission.update();
    this.trigger("updateProgress");
};

SeasonPassLogic.prototype.debugAddPoints = function () {
    if (this.isChestActive()) {
        this.addPoints(Math.max(1, this.getChestThreshold() - this.points));
        return;
    }

    if (!this.isCompleted()) {
        this.addPoints(Math.max(1, this.getStageCost(this.stage) - this.points));
    }
};

SeasonPassLogic.prototype.awardPoints = function (amount) {
    if (!this.canEarnPoints()) {
        return;
    }

    if (!this.pending) {
        this.pending = { stage: this.stage, points: this.points, amount: 0 };
    }

    this.pending.amount += amount;
    this.addPoints(amount);
};

SeasonPassLogic.prototype.awardQuestPoints = function (quest) {
    this.awardPoints(this.getQuestRewardPoints(quest));
};

SeasonPassLogic.prototype.consumePending = function () {
    this.pending = null;
    this.mission.update();
};

SeasonPassLogic.prototype.getDisplayedProgress = function () {
    if (this.pending) {
        return { stage: this.pending.stage, points: this.pending.points };
    }
    return { stage: this.stage, points: this.points };
};

SeasonPassLogic.prototype.getScrollStage = function () {
    if (this.pending) {
        return this.pending.stage;
    }

    const unclaimedStage = this.getLowestUnclaimedStage();
    return unclaimedStage !== undefined ? unclaimedStage : this.stage;
};

SeasonPassLogic.prototype.getQuestRewardPoints = function (quest) {
    if (this.isSuperUnlocked()) {
        return Math.ceil(quest.rewardPoints * SeasonPassLogic.SUPER_PASS_POINTS_MULTIPLIER);
    }
    return quest.rewardPoints;
};

SeasonPassLogic.prototype.canEarnPoints = function () {
    return !this.isCompleted() || this.isSuperUnlocked();
};

SeasonPassLogic.prototype.isStageClaimed = function (stage, vip) {
    const mask = vip ? this.claimedVip : this.claimed;
    return Math.floor(mask / Math.pow(2, stage)) % 2 === 1;
};

SeasonPassLogic.prototype.canClaimStage = function (stage, vip) {
    if (stage > this.stage || (vip && !this.isVipUnlocked())) {
        return false;
    }
    return !this.isStageClaimed(stage, vip);
};

SeasonPassLogic.prototype.getLowestUnclaimedStage = function () {
    const stages = this.getStages();
    const vipUnlocked = this.isVipUnlocked();

    for (let s = 0; s <= this.stage; s++) {
        const stageConfig = stages[s];
        if (!stageConfig) {
            continue;
        }

        if (stageConfig.free && !this.isStageClaimed(s, false)) {
            return s;
        }

        if (vipUnlocked && stageConfig.vip && !this.isStageClaimed(s, true)) {
            return s;
        }
    }

    return undefined;
};

SeasonPassLogic.prototype.canRemove = function () {
    return true;
};

SeasonPassLogic.prototype.claimStage = function (stage, vip) {
    if (!this.canClaimStage(stage, vip)) {
        return;
    }

    if (vip) {
        this.claimedVip += Math.pow(2, stage);
    } else {
        this.claimed += Math.pow(2, stage);
    }

    const stageConfig = this.getStages()[stage];
    const reward = stageConfig && (vip ? stageConfig.vip : stageConfig.free);
    if (reward) {
        this.logReward(reward, `${stage}`);
    }

    this.mission.update();
    this.trigger("stageClaimed", stage, vip);
};

SeasonPassLogic.prototype.onStageRewardsCollected = function (stage, vip) {
    this.trigger("stageRewardsCollected", stage, vip);
};

SeasonPassLogic.prototype.isVipUnlocked = function () {
    return this.vip >= 1;
};

SeasonPassLogic.prototype.isSuperUnlocked = function () {
    return this.vip >= 2;
};

SeasonPassLogic.prototype.unlockVip = function () {
    if (this.isVipUnlocked()) {
        return;
    }
    this.vip = 1;
    this.mission.update();
    this.trigger("vipUnlocked");
};

SeasonPassLogic.prototype.getLastChanceProduct = function () {
    return Product.CreateById(SeasonPassLogic.VIP_PRODUCT_KEY, {
        type: "season_pass_last_chance",
        mission: this.mission.type,
        period: this.period,
        alwaysAds: true
    });
};

SeasonPassLogic.prototype.buyLastChance = function (callback) {
    this.getLastChanceProduct().buy((success) => {
        if (success) {
            const mission = cleverapps.missionManager.findByType(this.mission.type);
            if (mission) {
                mission.logic.applyLastChance();
            }
        }
        callback && callback(success);
    });
};

SeasonPassLogic.prototype.applyLastChance = function () {
    this.logEvent(cleverapps.EVENTS.SEASON_PASS.BUY_LAST_CHANCE);
    this.unlockVip();
};

SeasonPassLogic.prototype.applyVipPass = function () {
    this.logEvent(cleverapps.EVENTS.SEASON_PASS.BUY_VIP);
    this.unlockVip();
    return this.getVipPassRewards();
};

SeasonPassLogic.prototype.applySuperPass = function () {
    this.logEvent(cleverapps.EVENTS.SEASON_PASS.BUY_SUPER);
    this.unlockVip();
    this.unlockSuper();
    this.awardPoints(SeasonPassLogic.SUPER_PASS_BONUS_POINTS);
    return this.getVipPassRewards();
};

SeasonPassLogic.prototype.applySuperUpgrade = function () {
    this.logEvent(cleverapps.EVENTS.SEASON_PASS.BUY_UPGRADE);
    this.unlockSuper();
    this.awardPoints(SeasonPassLogic.SUPER_PASS_BONUS_POINTS);
};

SeasonPassLogic.prototype.getVipPassProduct = function () {
    return Product.CreateById(SeasonPassLogic.VIP_PASS_PRODUCT_KEY, {
        type: "season_pass_vip",
        mission: this.mission.type,
        period: this.period,
        alwaysAds: true
    });
};

SeasonPassLogic.prototype.getSuperPassProduct = function () {
    return Product.CreateById(SeasonPassLogic.SUPER_PASS_PRODUCT_KEY, {
        type: "season_pass_super",
        mission: this.mission.type,
        period: this.period,
        alwaysAds: true
    });
};

SeasonPassLogic.prototype.getSuperUpgradeProduct = function () {
    return Product.CreateById(SeasonPassLogic.SUPER_UPGRADE_PRODUCT_KEY, {
        type: "season_pass_super_upgrade",
        mission: this.mission.type,
        period: this.period,
        alwaysAds: true
    });
};

SeasonPassLogic.prototype.isUpgradeMode = function () {
    return this.isVipUnlocked() && !this.isSuperUnlocked();
};

SeasonPassLogic.prototype.getVipPassPrice = function () {
    const product = this.getVipPassProduct();
    return (product && product.getCurrentPrice && product.getCurrentPrice()) || "$4.99";
};

SeasonPassLogic.prototype.getSuperPassPrice = function () {
    const upgrade = this.isUpgradeMode();
    const product = upgrade ? this.getSuperUpgradeProduct() : this.getSuperPassProduct();
    return (product && product.getCurrentPrice && product.getCurrentPrice()) || (upgrade ? "$2.99" : "$9.99");
};

SeasonPassLogic.prototype.getVipPassRewards = function () {
    return { energy: SeasonPassLogic.VIP_PASS_ENERGY };
};

SeasonPassLogic.prototype.grantPassRewards = function (rewards) {
    cleverapps.focusManager.display({
        focus: "SeasonPassPurchaseReward",
        stack: true,
        action: function (f) {
            new RewardWindow(rewards, {
                event: cleverapps.EVENTS.EARN.PURCHASE,
                toBubbleStack: true,
                toMainWorld: !cleverapps.meta.getSelectedLocation().locationConfig.isMain
            });
            cleverapps.focusManager.onceNoWindowsListener = f;
        }
    });
};

SeasonPassLogic.prototype.buyVipPass = function (callback) {
    const product = this.getVipPassProduct();
    if (!product) {
        callback && callback(false);
        return;
    }

    product.buy((success) => {
        if (success) {
            const mission = cleverapps.missionManager.findByType(this.mission.type);
            if (mission) {
                this.grantPassRewards(mission.logic.applyVipPass());
            }
        }
        callback && callback(success);
    }, { noRewardWindow: true });
};

SeasonPassLogic.prototype.buySuperPass = function (callback) {
    const product = this.getSuperPassProduct();
    if (!product) {
        callback && callback(false);
        return;
    }

    product.buy((success) => {
        if (success) {
            const mission = cleverapps.missionManager.findByType(this.mission.type);
            if (mission) {
                this.grantPassRewards(mission.logic.applySuperPass());
            }
        }
        callback && callback(success);
    }, { noRewardWindow: true });
};

SeasonPassLogic.prototype.buySuperUpgrade = function (callback) {
    const product = this.getSuperUpgradeProduct();
    if (!product) {
        callback && callback(false);
        return;
    }

    product.buy((success) => {
        if (success) {
            const mission = cleverapps.missionManager.findByType(this.mission.type);
            if (mission) {
                mission.logic.applySuperUpgrade();
            }
        }
        callback && callback(success);
    }, { noRewardWindow: true });
};

SeasonPassLogic.prototype.unlockSuper = function () {
    if (this.isSuperUnlocked()) {
        return;
    }
    this.vip = 2;
    this.mission.update();
    this.trigger("superUnlocked");
};

SeasonPassLogic.prototype.hasLastChance = function () {
    return !this.isVipUnlocked() && this.stage >= 1
        && bundles[`season_pass_last_chance_window_${this.period}`]
        && this.getLastChanceRewards();
};

SeasonPassLogic.prototype.beforeRemove = function (f) {
    const rewards = this.getAllUnclaimedRewards();
    if (!rewards) {
        f();
        return;
    }

    cleverapps.focusManager.compound(f, [
        (f) => {
            new RewardWindow(rewards, {
                event: cleverapps.EVENTS.EARN.OTHER,
                toBubbleStack: true,
                toMainWorld: !cleverapps.meta.getSelectedLocation().locationConfig.isMain
            });
            cleverapps.focusManager.onceNoWindowsListener = f;
        }
    ]);
};

SeasonPassLogic.prototype.getAllUnclaimedRewards = function () {
    const stages = this.getStages();
    const vipUnlocked = this.isVipUnlocked();

    let rewards = {};
    for (let s = 0; s <= this.stage; s++) {
        const stageConfig = stages[s];
        if (!stageConfig) {
            continue;
        }

        if (stageConfig.free && !this.isStageClaimed(s, false)) {
            rewards = RewardsList.sumRewards(rewards, stageConfig.free);
        }

        if (vipUnlocked && stageConfig.vip && !this.isStageClaimed(s, true)) {
            rewards = RewardsList.sumRewards(rewards, stageConfig.vip);
        }
    }

    if (Object.keys(rewards).length === 0) {
        return undefined;
    }

    return rewards;
};

SeasonPassLogic.prototype.claimAllVipStages = function () {
    if (!this.isVipUnlocked()) {
        return {};
    }
    const stages = this.getStages();

    let totals = {};
    for (let s = 0; s <= this.stage; s++) {
        if (this.isStageClaimed(s, true)) {
            continue;
        }
        const stageConfig = stages[s];
        if (!stageConfig || !stageConfig.vip) {
            continue;
        }
        totals = RewardsList.sumRewards(totals, stageConfig.vip);
        this.claimedVip += Math.pow(2, s);
    }

    if (Object.keys(totals).length > 0) {
        this.logReward(totals);
    }

    this.mission.update();
    this.trigger("vipStagesBulkClaimed");
    return totals;
};

SeasonPassLogic.prototype.getLastChanceRewards = function () {
    const stages = this.getStages();

    let totals = {};
    for (let s = 0; s <= this.stage; s++) {
        if (this.isStageClaimed(s, true)) {
            continue;
        }
        const stageConfig = stages[s];
        if (!stageConfig || !stageConfig.vip) {
            continue;
        }
        totals = RewardsList.sumRewards(totals, stageConfig.vip);
    }

    if (Object.keys(totals).length === 0) {
        return undefined;
    }

    return totals;
};

SeasonPassLogic.prototype.isCompleted = function () {
    return this.stage >= SeasonPassLogic.STAGES_AMOUNT - 1;
};

SeasonPassLogic.prototype.getChestThreshold = function () {
    return SeasonPassLogic.CHEST_BASE_THRESHOLD + SeasonPassLogic.CHEST_THRESHOLD_STEP * this.chestClaims;
};

SeasonPassLogic.prototype.isChestActive = function () {
    return this.isSuperUnlocked() && this.isCompleted();
};

SeasonPassLogic.prototype.canClaimChest = function () {
    return this.isChestActive() && this.points >= this.getChestThreshold();
};

SeasonPassLogic.prototype.claimChest = function () {
    if (!this.canClaimChest()) {
        return undefined;
    }

    this.points -= this.getChestThreshold();
    this.chestClaims += 1;

    this.logEvent(cleverapps.EVENTS.SEASON_PASS.CHEST);
    this.logReward(this.getChestReward(), "chest");

    this.mission.update();
    this.trigger("chestClaimed");
    this.trigger("updateProgress");

    return this.getChestReward();
};

SeasonPassLogic.prototype.getTimeLeft = function () {
    return this.mission.getTimeLeft();
};

SeasonPassLogic.prototype.getQuestsTimeLeft = function () {
    return Math.min(this.questPack.getTimeLeft(), this.mission.getTimeLeft());
};

SeasonPassLogic.prototype.isAttentionRequired = function () {
    return this.getClaimableRewardsCount() > 0;
};

SeasonPassLogic.prototype.getClaimableRewardsCount = function () {
    let count = 0;
    const vipUnlocked = this.isVipUnlocked();
    for (let s = 0; s <= this.stage; s++) {
        if (!this.isStageClaimed(s, false)) {
            count++;
        }
        if (vipUnlocked && !this.isStageClaimed(s, true)) {
            count++;
        }
    }
    return count;
};

SeasonPassLogic.prototype.getAttentionCounter = function () {
    return this.getClaimableRewardsCount();
};

SeasonPassLogic.prototype.getAttentionCounterFrame = function () {
    return bundles.season_pass_questsgroupicon_prefab && bundles.season_pass_questsgroupicon_prefab.frames.season_pass_amount_bg;
};

SeasonPassLogic.prototype.onUpdateMission = function () {
    this.mission.updateMissionIcon();
};

SeasonPassLogic.prototype.onPressed = function () {
    const mission = this.mission;

    cleverapps.focusManager.display({
        focus: "SeasonPassMissionIconClicked",
        action: (f) => {
            if (!mission.isRunning() && (!mission.tillTheTimeEnds || mission.getTimeLeft() === 0)) {
                mission.displayCompleted(f);
                return;
            }

            mission.skipStartWindow();

            if (this.questPack.needShowNewTasks && this.canEarnPoints()) {
                this.questPack.needShowNewTasks = false;
                this.showNewTasksWindow(() => this.showMainWindow(f));
            } else {
                this.showMainWindow(f);
            }
        }
    });
};

SeasonPassLogic.prototype.showNewTasksWindow = function (next) {
    new SeasonPassNewTasksWindow(this.mission);
    cleverapps.focusManager.onceNoWindowsListener = next;
};

SeasonPassLogic.prototype.hasCompletedTasksToShow = function () {
    return this.questPack.hasCompletedPending() && this.canEarnPoints();
};

SeasonPassLogic.prototype.showCompletedTasksWindow = function (next) {
    new SeasonPassCompletedTasksWindow(this.mission);
    cleverapps.focusManager.onceNoWindowsListener = () => {
        this.questPack.acknowledgeCompleted();
        next();
    };
};

SeasonPassLogic.prototype.showMainWindow = function (next) {
    if (this.hasCompletedTasksToShow()) {
        this.showCompletedTasksWindow(() => this.showMainWindow(next));
        return;
    }

    new SeasonPassWindow(this.mission);
    cleverapps.focusManager.onceNoWindowsListener = next;
};

// the first day of the season is day 1
SeasonPassLogic.prototype.getSeasonDay = function () {
    return Math.floor((Date.now() - this.mission.started) / cleverapps.parseInterval("1 day")) + 1;
};

SeasonPassLogic.prototype.markNudges = function (count) {
    this.nudges = count;
    this.mission.update();
};

SeasonPassLogic.prototype.getTierSuffix = function () {
    if (this.isSuperUnlocked()) {
        return "super";
    }
    return this.isVipUnlocked() ? "vip" : "free";
};

SeasonPassLogic.prototype.logReward = function (reward, itemId) {
    this.logEvent(cleverapps.EVENTS.SEASON_PASS.RWCOST + this.getTierSuffix(), { value: RewardsList.calcCost(reward) });
    this.mission.logEvent(Mission.EVENTS.MISSION_REWARD_VALUE, { reward, itemId });
};

SeasonPassLogic.prototype.logEvent = function (name, options) {
    options = options || {};

    const params = {};
    if (options.value !== undefined) {
        params.value = options.value;
    }

    const finish = this.mission.finishTime(true);
    if (finish) {
        params.event = Math.min(Date.now(), finish - 1);
    }

    if (options.once) {
        if (options.key) {
            params.key = options.key;
        }
        cleverapps.playSession.logEventOnce(name, params);
        return;
    }

    cleverapps.metrics.logEvent(name, params);
};

SeasonPassLogic.prototype.destructor = function () {
    clearTimeout(this.questPack.refreshTimeout);
    runCleaners(this);
};

SeasonPassLogic.VIP_PRODUCT_KEY = "kit5";

SeasonPassLogic.VIP_PASS_PRODUCT_KEY = "kit5";
SeasonPassLogic.SUPER_PASS_PRODUCT_KEY = "kit10";
SeasonPassLogic.SUPER_UPGRADE_PRODUCT_KEY = "kit3";

SeasonPassLogic.VIP_PASS_ENERGY = 150;
SeasonPassLogic.SUPER_PASS_BONUS_POINTS = 2000;
SeasonPassLogic.SUPER_PASS_POINTS_MULTIPLIER = 1.5;

SeasonPassLogic.CHEST_BASE_THRESHOLD = 4500;
SeasonPassLogic.CHEST_THRESHOLD_STEP = 500;

SeasonPassLogic.STAGES_AMOUNT = 45;
SeasonPassLogic.MONTHS = ["june", "august", "september", "october", "november"];

SeasonPassLogic.getPeriod = function () {
    const event = cleverapps.eventManager.getFeatureEvent("season_pass");
    if (event && event.options && event.options.period
            && SeasonPassLogic.MONTHS.indexOf(event.options.period) !== -1) {
        return event.options.period;
    }

    return undefined;
};

SeasonPassLogic.listWindowBundles = function (mission) {
    const bundle = `season_pass_${mission.logic.period}`;
    return (bundles[bundle] ? [bundle] : []).concat(!cleverapps.meta.getSelectedLocation().locationConfig.isMain && bundles.main_world_reward_units ? ["main_world_reward_units"] : []);
};

SeasonPassLogic.getSideBarJson = function (period) {
    const bundle = bundles[`season_pass_sidebar_${period}`];
    return bundle && bundle.jsons[`season_pass_icon_${period}`] || bundles.season_pass_sidebar.jsons.season_pass_icon;
};

SeasonPassLogic.RestoreGive = function (apply) {
    return function (mission) {
        const rewards = apply.call(mission.logic);
        if (rewards) {
            cleverapps.paymentsLogic.showRestoredWindow(rewards);
        }
    };
};

Restorable.BY_TYPE["season_pass_last_chance"] = new Restorable({
    location: Restorable.LocationMission,
    find: Restorable.FindRunningMission,
    give: SeasonPassLogic.RestoreGive(SeasonPassLogic.prototype.applyLastChance)
});

Restorable.BY_TYPE["season_pass_vip"] = new Restorable({
    location: Restorable.LocationMission,
    find: Restorable.FindRunningMission,
    give: SeasonPassLogic.RestoreGive(SeasonPassLogic.prototype.applyVipPass)
});

Restorable.BY_TYPE["season_pass_super"] = new Restorable({
    location: Restorable.LocationMission,
    find: Restorable.FindRunningMission,
    give: SeasonPassLogic.RestoreGive(SeasonPassLogic.prototype.applySuperPass)
});

Restorable.BY_TYPE["season_pass_super_upgrade"] = new Restorable({
    location: Restorable.LocationMission,
    find: Restorable.FindRunningMission,
    give: SeasonPassLogic.RestoreGive(SeasonPassLogic.prototype.applySuperUpgrade)
});
